import {
  Address,
  Roster,
  RosterResponse,
  User
} from "@/api/dto/dto";
import {RosterSelectionState} from "@admin/RosterSelection";
import PermissionsHelpers from "@/helpers/permissions_helpers";
import {nonGroupedValues} from "@common/FilterComponent";
import {Value} from "patient-ping-remedy/packages/dropdown-list/lib/DropdownList";
import {StateConfig} from "@/api/dto/state-config";
import {
  DataPermission,
  DataRole,
  Feature,
  FeatureNew,
  FeaturePermission,
  FeaturePermissionLevel,
  FeatureRole
} from "@/api/dto/permissions";

export const dataSourceValues  = (stateConfig: StateConfig | undefined) : Value[] => ([
  {label: 'ADT', value: 'ADT'},
  {label: stateConfig?.branding?.openBeds.referral.service || "", value: 'OB_REFERRALS'},
  {label: stateConfig?.branding.openBeds.crisis.service || "", value: 'OB_CRISIS'},
  {label: stateConfig?.branding.findHelp.service || "", value: 'FINDHELP'},
  {label: 'PDMP', value: 'PDMP'},
  {label: 'Justice involved', value: 'JI'}
]);

export const featureValues: Value[] = [
  {label: 'Client list', value: 'CLIENT_LIST'},
  {label: 'Client profile', value: 'CLIENT_PROFILE'},
  {label: 'Forms', value: 'FORMS'},
  {label: 'User management', value: 'USER_PERMISSIONS'},
  {label: 'Notifications', value: 'NOTIFICATIONS'},
  {label: 'Tasks', value: 'TASKS'},
  {label: 'Referrals', value: 'REFERRALS'},
  {label: 'Network registry', value: 'NETWORK_REGISTRY'},
];

const RosterHelpers = {
  getRosterById(rosters: RosterResponse | undefined, id: string | number | undefined | null) {
    if(!rosters || !id) return;

    return rosters?.data?.data?.find((roster: Roster) => roster.id === parseInt(id as string));
  },
  determineAvailableRosterOptions(
    form: { [key: string]: any },
    rosters: RosterResponse | undefined,
    currentUser: User | null,
    isNewPermissionsEnabled: boolean
  ) {
    if(!currentUser || !rosters) return [];

    const rosterOptionsNotInForm = rosters?.rosterOptions.filter(roster => {
      return !form.rosters?.value.map((set: RosterSelectionState ) => set.roster.value).includes(roster.value);
    });

    if (PermissionsHelpers.hasAdminRole(currentUser!)) {
      return rosterOptionsNotInForm ?? [];
    } else {
      const rosterOptionsUserCanManage = rosterOptionsNotInForm?.filter(roster => {
        const rosterData = this.getRosterById(rosters, roster.value);

        return isNewPermissionsEnabled ?
          PermissionsHelpers.canForUserFeaturePermissionRosterNew(currentUser!, rosterData!,
            FeaturePermissionLevel.MANAGE,
            FeatureNew.ADMIN_MANAGEMENT) :
          PermissionsHelpers.canForUserFeaturePermissionRoster(currentUser!, rosterData!,
            FeaturePermissionLevel.MANAGE,
            Feature.USER_PERMISSIONS);
      });

      return rosterOptionsUserCanManage ?? [];
    }
  },
  getFeatureRoleOptions(featureRoleOptions: Value[]) {
    const apiFeatureRoleOptions : nonGroupedValues[] = featureRoleOptions.map((option: Value) => {
      return { value: option.label, label: option.label, id: option.value };
    }) ?? [];

    return [...apiFeatureRoleOptions,
      {value: 'Bamboo Admin', label: 'Bamboo Admin', id: 'bamboo_admin'},
      {value: 'Super Admin', label: 'Super Admin', id: 'super_admin'}
    ];
  },
  getDataRoleOptions(dataRoleOptions: Value[]) {
    const apiDataRoleOptions : nonGroupedValues[] = dataRoleOptions.map((option: Value) => {
      return { value: option.label, label: option.label, id: option.value };
    }) ?? [];

    return [...apiDataRoleOptions,
      {value: 'Bamboo Admin', label: 'Bamboo Admin', id: 'bamboo_admin'},
      {value: 'Super Admin', label: 'Super Admin', id: 'super_admin'}
    ];
  },
  getOrganizationOptions(organizationOptions: Value[]) {
    const apiOrganizationOptions : nonGroupedValues[] = organizationOptions.map((option: Value) => {
      return { value: option.label, label: option.label, id: option.value };
    }) ?? [];

    return apiOrganizationOptions;
  },
  getRosterOptions(rosterOptions: Value[]) {
    const apiRosterOptions : nonGroupedValues[] = rosterOptions.map((option: Value) => {
      return { value: option.label, label: option.label, id: option.value };
    }) ?? [];

    return apiRosterOptions;
  },
  getUserFiltersDropdownOptions(rosters: RosterResponse | undefined) {
    return {
      dataRoleOptions: this.getDataRoleOptions(rosters?.dataRoleOptions ?? []),
      featureRoleOptions: this.getFeatureRoleOptions(rosters?.featureRoleOptions ?? []),
      organizationOptions: this.getOrganizationOptions(rosters?.organizationOptions ?? []),
      rosterOptions: this.getRosterOptions(rosters?.rosterOptions ?? []),
      statusOptions: [
        { value: 'enabled', label: 'Enabled', id: 'enabled' },
        { value: 'disabled', label: 'Disabled', id: 'disabled' }
      ],
      ssoOptions: [
        { value: 'enabled', label: 'Enabled', id: 'enabled' },
        { value: 'disabled', label: 'Disabled', id: 'disabled' }
      ]
    };
  },
  getGroupFiltersDropdownOptions(rosters: RosterResponse | undefined) {
    return {
      organizationOptions: this.getOrganizationOptions(rosters?.organizationOptions ?? []),
    };
  },
  formatGroupAddress(address: Address | null | undefined): string {
    if (!address) {
      return '';
    }

    const {address1, city, state, zip} = address;
    return `${address1}, ${city}, ${state} ${zip}`;
  },
  groupTypeToSubmission(groupType: string): string {
    switch (groupType) {
    case "SNF":
    case "HHA":
    case "LTAC":
    case "REHAB_HOS":
    case "HOSPICE":
    case "ALF":
    case "HL7_BASED_PROGRAM":
    case "CCRC":
    case "ADH":
    case "URGENT_CARE":
    case "PALLIATIVE":
    case "CRISIS":
    case "HOS":
      return "Dynamic (Event Based) Roster";
    case "OWNING_ORG":
    case "PRIVATE_PROGRAM":
      return "Static Roster";
    default:
      return "Dynamic (Event Based) Roster";
    }
  },
  getAllDataPermissions(data_roles: DataRole[]): DataPermission[] {
    const dataPermissions: DataPermission[] = [];
    data_roles.forEach(
      data_role =>
        data_role.permissions.forEach(
          permission => dataPermissions.push(permission)
        )
    );

    return dataPermissions;
  },
  getAllFeaturePermissions(feature_roles: FeatureRole[]): FeaturePermission[] {
    const featurePermissions: FeaturePermission[] = [];
    feature_roles.forEach(
      feature_role =>
        feature_role.permissions.forEach(
          permission => featurePermissions.push(permission)
        )
    );

    return featurePermissions;
  },
};

export default RosterHelpers;
